/* =============================================================================
   VIEWS / ADMIN / OCCUPANCY — Belegungs-Übersicht (Timeline + Snapshot)
   =============================================================================
   - Timeline: Tisch × 30-Min-Slot, klickbare belegte Slots
   - Snapshot: Tischplan zum gewählten Zeitpunkt, frei/belegt-Visualisierung
   - Modal-Helpers: openOccupancyResModal (Klick auf belegt),
                    openOccupancyTableModal (Klick auf frei + Walk-In-Shortcut)
   - startWalkInAtTable: triggert openWalkInForm mit Pre-Selection
============================================================================ */

import {
  htmlEscape, fmtDate, fmtEUR, todayISO, tomorrowISO,
  timeToMinutes, minutesToTime, nowSnappedToHalfHour,
  normalizePreorderItems, preorderTotal, preorderHasItems, preorderSpecialRequests
} from '../../utils/helpers.js';
import { closeModal } from '../../utils/dom.js';
import {
  getCurrentSlotDuration,
  getReservationDuration,
  effectiveReservationEndMin,
  reservationOverlaps,
  getOccupiedTableIds,
  getTableSharingState,
  tableExtraChairs,
  isConfirmedSlot
} from '../../core/capacity.js';
import { naturalCompare, getSlotStepMin } from '../../core/reservation-engine.js';
import { effectiveOccupancyEndMs, isOccupancyOverdue, occupancyOverdueMinutes } from '../../core/table-occupancies.js';
// S199 Cut 2 — Struktur-Elemente als Hintergrund-Ebene im Snapshot. Wiederverwendung
// der Editor-Renderer (renderElementStatic, statisch/non-interaktiv) + pure Helfer.
import { renderElementStatic, elementsForArea, canvasSizeForArea, planCanvasHtml, contentBBox } from './layout-editor.js';
import { fitAllPlans } from '../../utils/plan-fit.js';
// S239 Cut A0 — Reservierungs-Check-in „Gäste sind da".
import { findCheckinOccupancy, reservationCheckinButton } from './reservation-checkin.js';
import { operatorCan } from '../../core/operator-roles.js';
import { isTableSelfOrder } from '../../core/order-sources.js';
// S245 Cut C — Hinweisband + Kachel-Badges aus `table_signals`.
import { mountSignalBand, renderSignalBandInto, openSignals, signalsForTable,
         onSignalsChanged,
         refreshSignals, signalAgeText } from './table-signals.js';
// S243 Cut A — Schloss „Handy-Bestellung am Tisch". `selfOrderState` ist der
// Client-Spiegel des SQL-Helpers `occupancy_self_order_state` (phase_176).
import { selfOrderState, selfOrderView } from '../../core/table-qr.js';

function getState() {
  return (window.App && window.App.storage && window.App.storage._getInternalState())
         || { reservations: [], tables: [], settings: {} };
}

/** S239 Cut A0 — darf der aktive Operator einchecken? Spiegelt das Server-Gate
 *  (`assert_operator_role(['manager','service'])`) über die ACTION_MATRIX;
 *  owner/admin überlagern dort ohnehin. Ohne Operator-Layer: erlauben (der
 *  Server bleibt die Wahrheit, die UI blockiert dann nicht grundlos). */
function _mayCheckin() {
  const api = (typeof window !== 'undefined') && window.App && window.App.operator;
  const op = (api && typeof api.getOperator === 'function') ? api.getOperator() : null;
  if (!op) return true;
  return operatorCan(op, 'reservation.checkin');
}

/** S243 Cut A — darf der aktive Operator das Schloss schalten? Spiegelt das
 *  Server-Gate (`assert_operator_role(['manager','service'])`) über die
 *  ACTION_MATRIX. Ohne Operator-Layer: erlauben — der Server bleibt die
 *  Wahrheit, die UI soll nicht grundlos blockieren (wie _mayCheckin). */
function _maySelfOrder() {
  const api = (typeof window !== 'undefined') && window.App && window.App.operator;
  const op = (api && typeof api.getOperator === 'function') ? api.getOperator() : null;
  if (!op) return true;
  return operatorCan(op, 'occupancy.selforder');
}

/* S244 Cut B — Gast-Entwürfe je Belegung + Sperr-Scans, beides asynchron
   nachgeladen und danach EINMAL nachgezeichnet. Modul-State, damit ein
   Realtime-Re-Render sie nicht verliert (kein View-Hijack). */
let _guestCartCounts = {};
let _sideloadAt = 0;

/** Beides in einem Rutsch nachladen — höchstens alle 20 s, damit ein
 *  Realtime-Sturm nicht in einen Query-Sturm umschlägt. */
function _sideloadTableQrInfo() {
  const now = Date.now();
  if (now - _sideloadAt < 20000) return;
  _sideloadAt = now;
  const occ = window.App && window.App.storage && window.App.storage.tableOccupancies;
  // S245 Cut C: die Sperr-Scans kommen nicht mehr aus `audit_log`, sondern über
  // `table-signals.js` per Realtime. Hier bleibt nur der Gast-Entwurf.
  refreshSignals();
  if (!occ || typeof occ.guestCartCounts !== 'function') return;
  occ.guestCartCounts()
    .then(c => {
      _guestCartCounts = c || {};
      try { renderOccupancySnapshot(); } catch (_) { /* Tab gewechselt */ }
    })
    .catch(e => console.warn('[occupancy] Gast-Entwuerfe:', e && e.message));
}

/** Hat diese Belegung einen noch nicht abgesendeten Gast-Entwurf? */
function _guestDraftOf(occ) {
  return (occ && _guestCartCounts[occ.id]) || null;
}

/** S243 Cut A — Schloss-Zustand einer Belegung aus dem Cache berechnen.
 *  Ein Aufruf pro Belegung, kein RPC: die Kachel rendert bis zu 60 Tische. */
function _selfOrderStateOf(occ) {
  const state = getState();
  return selfOrderState({
    occupancy: occ,
    settings: state.settings,
    reservations: state.reservations
  });
}

// Globaler State für Datum/Schnappschuss-Zeit. window-exposed weiter unten.
let occupancyDate = todayISO();
let occupancySnapTime = null;

// -----------------------------------------------------------------------------
// S199 Cut 2 / S217 Cut 2 — Belegung zeigt nur EINEN Bereich (3er-Segment
// Innen/Außen/Club) — für BEIDE Karten (Timeline + Snapshot) über EINE gemeinsame
// Raum-Wahl. Diese lebt als MODUL-State — NICHT im DOM, damit ein Realtime-
// Re-Render (rerenderActiveTab → renderOccupancy) sie nicht zurücksetzt (Memories
// feedback_gg_no_view_hijack / feedback_gg_non_destructive_render).
// S217 Cut 2 (Jonas-Entscheid): KEIN localStorage mehr — die Wahl bleibt innerhalb
// der Sitzung erhalten (letzte Wahl, kein Hijack), springt aber nach jedem
// Page-Reload wieder auf Innen (Modul-Init 'indoor'). Timeline + Snapshot teilen
// exakt denselben State und rendern beide neu, egal welches Segment geklickt wird.
// -----------------------------------------------------------------------------
const SNAP_AREAS = [
  { id: 'indoor',  label: 'Innen', full: 'Innenbereich' },
  { id: 'outdoor', label: 'Außen', full: 'Außenbereich' },
  { id: 'club',    label: 'Club',  full: 'Clubraum' }
];

/** Welche Bereiche sind aktuell verfügbar (Innen immer, Außen/Club nach Settings)? */
export function enabledSnapAreas(settings) {
  const s = settings || {};
  return SNAP_AREAS.filter(a =>
    a.id === 'indoor' || (a.id === 'outdoor' ? s.outdoorEnabled : s.clubEnabled));
}

/** Pure: gewünschten Bereich gegen die verfügbaren auflösen. Default/Fallback =
 *  erster verfügbarer (i. d. R. Innen), wenn die Präferenz unbekannt/deaktiviert ist. */
export function resolveSnapArea(pref, settings) {
  const avail = enabledSnapAreas(settings);
  const ids = avail.map(a => a.id);
  if (pref && ids.includes(pref)) return pref;
  return ids[0] || 'indoor';
}

// Default Innen bei jedem Page-Load (kein localStorage); innerhalb der Sitzung
// überlebt die Wahl als Modul-State jeden Re-Render.
let occupancySnapArea = 'indoor';

/** S217 Cut 2 — gemeinsames Bereich-Segment (identisch für Timeline + Snapshot).
 *  Klick auf jedes Segment ruft setOccupancySnapArea → beide Karten rendern neu. */
function _areaSegHtml(areaId, availAreas) {
  return `<div class="occ-seg" role="tablist" aria-label="Bereich">${availAreas.map(a =>
    `<button type="button" class="occ-seg-btn${a.id === areaId ? ' active' : ''}" role="tab"
       aria-selected="${a.id === areaId ? 'true' : 'false'}"
       onclick="setOccupancySnapArea('${a.id}')">${htmlEscape(a.label)}</button>`).join('')}</div>`;
}

export function renderOccupancy() {
  // S60 W4 Hotfix — Today-Lock entfernt: Operator darf historische Tage anschauen
  // (Audit / Debug nach Mitternachts-Übergang). Default beim ersten Mount ist heute.
  if (!occupancyDate) occupancyDate = todayISO();
  if (!occupancySnapTime) occupancySnapTime = nowSnappedToHalfHour();

  // S141 — Schnappschuss-Zeitfeld rastert in derselben Granularität wie die
  // Timeline (step in Sekunden für <input type="time" step=...>).
  const snapStepSec = getSlotStepMin() * 60;

  // S199 Cut 2 — Karten-Reihenfolge: Räume (Snapshot) OBEN, Belegungskalender
  // (Timeline) UNTEN. Datum-Picker steuert beide und sitzt am Kopf des Räume-Blocks.
  const html = `
    <div class="card">
      <div class="occupancy-day-picker">
        <label>Datum:</label>
        <input type="date" value="${occupancyDate}" onchange="occupancyDate=this.value; renderOccupancy();">
        <button class="btn btn-secondary btn-sm" onclick="occupancyDate=todayISO(); renderOccupancy();">Heute</button>
        <button class="btn btn-secondary btn-sm" onclick="occupancyDate=tomorrowISO(); renderOccupancy();">Morgen</button>
      </div>
      <h2 style="margin-top:12px;">Räume zum gewählten Zeitpunkt</h2>
      <p style="color:var(--ink-soft); font-size:.9rem; margin-bottom: 14px; display:flex; align-items:center; gap:8px; flex-wrap:wrap;">
        Schnappschuss um
        <input type="time" id="snap-time" step="${snapStepSec}" value="${occupancySnapTime}" onchange="occupancySnapTime=this.value; renderOccupancySnapshot();" style="width:auto; padding: 4px 8px;"> Uhr
        <button class="btn btn-ghost btn-sm" onclick="occupancySnapTime=nowSnappedToHalfHour(); renderOccupancy();">Jetzt</button>
        <span style="color:var(--ink-muted); font-size:.85rem;">— klicke eine Kachel für Details oder Walk-In.</span>
      </p>
      <div id="snapshot-wrap"></div>
    </div>
    <div class="card" style="margin-top:20px;">
      <h2 style="margin:0 0 12px;">Belegungskalender</h2>
      <div id="timeline-wrap"></div>
      <div style="display:flex; gap: 16px; margin-top: 14px; font-size: .85rem; color: var(--ink-soft); flex-wrap: wrap;">
        <span><span style="display:inline-block; width:12px; height:12px; background:var(--green); border-radius:3px; vertical-align:middle;"></span> Reservierung bestätigt</span>
        <span><span style="display:inline-block; width:12px; height:12px; background:var(--warn); border-radius:3px; vertical-align:middle;"></span> Reservierung offen</span>
        <span><span style="display:inline-block; width:12px; height:12px; background:var(--warn-soft); border:1px solid var(--accent); border-radius:3px; vertical-align:middle;"></span> 🪑 Walk-In · 🤝 geteilter Tisch</span>
        <span><span style="display:inline-block; width:12px; height:12px; background:var(--bg-soft); border-radius:3px; vertical-align:middle;"></span> frei</span>
      </div>
    </div>
  `;
  const tab = document.getElementById('tab-occupancy');
  if (tab) tab.innerHTML = html;
  renderTimeline();
  renderOccupancySnapshot();
  // S196 Cut a — defensive Auto-Freigabe verwaister Walk-Ins beim Öffnen des
  // Belegung-Tabs (Sicherheitsnetz zusätzlich zum pg_cron alle 10 Min). Nur
  // Belegungen ohne offene unbezahlte Order + nach Dauer+15 Min werden beendet
  // (server-autoritativ, tenant-scoped). Fire-and-forget; Re-Render nur bei Treffer.
  const occAdapter = window.App && window.App.storage && window.App.storage.tableOccupancies;
  if (occAdapter && typeof occAdapter.autoReleaseStale === 'function') {
    occAdapter.autoReleaseStale().then(n => {
      if (n > 0 && document.getElementById('tab-occupancy')) {
        renderTimeline();
        renderOccupancySnapshot();
      }
    }).catch(() => {});
  }
}

function renderTimeline() {
  const state = getState();
  const areaLabelFn = (typeof window.areaLabel === 'function') ? window.areaLabel : (a => a || '');
  const wrap = document.getElementById('timeline-wrap');
  if (!wrap) return;
  // S141 — Granularität konfigurierbar (Single-Source getSlotStepMin).
  const step = getSlotStepMin();
  // S217 Cut 2 — Zeitachse = Kernzeiten (settings.openingTimeStart/End) statt hart
  // 10–24. Fenster auf ganze Slot-Schritte gerundet, damit Raster + minutengenaues
  // Balken-Overlay exakt fluchten. Fallback 10:00–24:00, wenn Settings fehlen/
  // unplausibel. Belegungen außerhalb bleiben unverändert gespeichert; die Anzeige
  // clippt am Achsen-Ende (überhängende/overdue Balken werden bis dorthin gezeichnet).
  const settings = state.settings || {};
  const openStartMin = timeToMinutes(settings.openingTimeStart || '11:00');
  const openEndMin   = timeToMinutes(settings.openingTimeEnd   || '23:00');
  const validWindow  = Number.isFinite(openStartMin) && Number.isFinite(openEndMin) && openEndMin > openStartMin;
  const timelineStartMin = validWindow ? openStartMin : 10 * 60;
  const rawEndMin        = validWindow ? openEndMin   : 24 * 60;
  const slotCount = Math.max(1, Math.ceil((rawEndMin - timelineStartMin) / step));
  const timelineEndMin = timelineStartMin + slotCount * step;   // Achsen-Ende (≥ Kernzeit-Ende)
  const totalMin       = timelineEndMin - timelineStartMin;     // = slotCount * step
  const hoursHtml = [];
  for (let i = 0; i < slotCount; i++) {
    const slotMin = timelineStartMin + i * step;
    const isHourStart = slotMin % 60 === 0;
    hoursHtml.push(`<div class="timeline-hour">${isHourStart ? (slotMin / 60) + ':00' : ''}</div>`);
  }
  let rows = `
    <div class="timeline-row header">
      <div>Tisch</div>
      <div class="timeline-hours">${hoursHtml.join('')}</div>
    </div>
  `;
  // S217 Cut 2 — Raum-Split: nur Tische des gewählten Bereichs (gemeinsame Wahl mit
  // dem Snapshot). Deaktivierten/unbekannten Bereich auf Innen normalisieren.
  const areaId = resolveSnapArea(occupancySnapArea, settings);
  if (areaId !== occupancySnapArea) occupancySnapArea = areaId;
  const tables = (state.tables || [])
    .filter(t => t.area === areaId)
    .slice()
    .sort((a, b) => naturalCompare(a.name, b.name));
  const dayRes = (state.reservations || []).filter(r =>
    r.date === occupancyDate && !r.deletedAt && r.status !== 'declined' && r.status !== 'cancelled'
  );
  // S60 W2 + Mitternachts-Hotfix — Walk-Ins die den ausgewählten Tag berühren.
  // Filter: started_at + duration_min überschneidet sich mit dem Tagesfenster
  // [occupancyDate 00:00, occupancyDate 24:00). Damit werden auch über-Mitternacht-
  // Walk-Ins (start 23:55, ende 01:25) am Folgetag korrekt angezeigt.
  const dayStartMs = new Date(occupancyDate + 'T00:00:00').getTime();
  const dayEndMs   = dayStartMs + 24 * 60 * 60 * 1000;
  // S134c (Befund 1+4) — completed-Belegungen MIT anzeigen (sie schrumpfen auf
  // ended_at, verschwinden nicht). Nur cancelled/deleted bleiben draußen.
  // S196 Cut a — effektives Ende via Single-Source: aktive Belegungen ohne
  // ended_at reichen bis max(geplant, jetzt), damit eine überzogene, noch nicht
  // freigegebene Belegung nicht aus der Timeline fällt (I2-Befund).
  // „jetzt" nur bei Heute-Ansicht: aktive Belegung ohne ended_at reicht bis
  // max(geplant, jetzt); an anderen Tagen keine Verlängerung (−∞ → planned).
  const nowRef = (occupancyDate === todayISO()) ? Date.now() : Number.NEGATIVE_INFINITY;
  const dayWalkIns = (state.tableOccupancies || []).filter(o => {
    if (o.deletedAt || !o.startedAt) return false;
    if (o.status !== 'active' && o.status !== 'completed') return false;
    const startMs = new Date(o.startedAt).getTime();
    if (isNaN(startMs)) return false;
    const endMs = effectiveOccupancyEndMs(o, nowRef);
    if (isNaN(endMs)) return false;
    return startMs < dayEndMs && endMs > dayStartMs;  // Überschneidung mit dem Tag
  });

  // S196-Fix (Jonas) — MINUTENGENAUE Balken statt slot-weiser Füllung: eine
  // Belegung/Reservierung wird exakt an ihrer Start-/End-Minute positioniert
  // (kein „Aufschnappen" auf volle 15-Min-Slots). Das Zeitraster bleibt als
  // Hintergrund (Stundenlinien); die Blöcke liegen als absolut positioniertes
  // Overlay darüber. left/width als % des Kernzeit-Fensters (S217 Cut 2).
  const clampPct = (v) => Math.max(0, Math.min(100, v));
  const toLeftPct = (minRel) => ((minRel - timelineStartMin) / totalMin) * 100;
  const fmtClock = (ms) => new Date(ms).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });

  for (const t of tables) {
    const isDisabled = t.disabled === true;
    // Hintergrund-Raster (Stundenlinien / außer-Betrieb-Schraffur) — ohne
    // Belegungs-Klassen; die Blöcke kommen als Overlay.
    const gridHtml = [];
    for (let i = 0; i < slotCount; i++) {
      let cls = 'timeline-slot';
      if ((timelineStartMin + i * step) % 60 === 0) cls += ' hour-mark';
      if (isDisabled) cls += ' tl-disabled';
      gridHtml.push(`<div class="${cls}"></div>`);
    }

    const bars = [];
    if (!isDisabled) {
      // Reservierungen (minutengenau; effektives Ende respektiert table_released_at).
      for (const r of dayRes) {
        if (!(r.assignedTables || []).includes(t.id)) continue;
        const rStart = timeToMinutes(r.time);
        const rEnd   = effectiveReservationEndMin(r, rStart);
        const vs = Math.max(timelineStartMin, rStart);
        const ve = Math.min(timelineEndMin, rEnd);
        if (ve <= vs) continue;
        const left  = clampPct(toLeftPct(vs));
        const width = clampPct(((ve - vs) / totalMin) * 100);
        const confirmed = isConfirmedSlot(r);
        const assigned = (r.assignedTables || []).slice();
        let label;
        if (assigned.length > 1) {
          const sorted = assigned.slice().sort(naturalCompare);
          label = (sorted[0] === t.id) ? (r.guests + 'P ↔') : '↔';
        } else {
          label = r.guests + 'P';
        }
        const title = `${r.time}–${minutesToTime(rEnd % (24 * 60))} · ${r.name} (${r.guests}P) — Klick für Details`;
        bars.push(`<div class="tl-bar tl-bar-${confirmed ? 'occupied' : 'pending'}" style="left:${left}%; width:${width}%" onclick="openOccupancyResModal('${r.id}')" title="${htmlEscape(title)}"><span class="tl-bar-label">${htmlEscape(label)}</span></div>`);
      }
      // Walk-Ins (minutengenau über absolute ms — cross-day-sicher, kein Runden).
      for (const o of dayWalkIns) {
        const matchTable = o.tableId === t.id
          || (Array.isArray(o.assignedTableIds) && o.assignedTableIds.includes(t.id));
        if (!matchTable) continue;
        const startMs = new Date(o.startedAt).getTime();
        const endMs   = effectiveOccupancyEndMs(o, nowRef);
        if (isNaN(startMs) || isNaN(endMs)) continue;
        const vs = Math.max(timelineStartMin, (startMs - dayStartMs) / 60000);
        const ve = Math.min(timelineEndMin, (endMs - dayStartMs) / 60000);
        if (ve <= vs) continue;
        const left  = clampPct(toLeftPct(vs));
        const width = clampPct(((ve - vs) / totalMin) * 100);
        const overdue = isOccupancyOverdue(o);
        let cls = 'tl-bar tl-bar-walkin';
        if (o.status === 'completed') cls += ' tl-completed';
        if (overdue) cls += ' tl-overdue';
        const title = `${fmtClock(startMs)}–${fmtClock(endMs)} · Walk-In ${o.guests}P${o.status === 'completed' ? ' (beendet)' : ''}${overdue ? ' · überfällig' : ''} — Klick für Details`;
        const label = (overdue ? '⏰ ' : '🪑 ') + o.guests + 'P';
        bars.push(`<div class="${cls}" style="left:${left}%; width:${width}%" onclick="openOccupancyWalkInModal('${htmlEscape(o.id)}')" title="${htmlEscape(title)}"><span class="tl-bar-label">${htmlEscape(label)}</span></div>`);
      }
    }

    const areaTag = `<span class="badge ${t.area==='outdoor'?'badge-completed':t.area==='club'?'badge-manual':'badge-confirmed'}" style="font-size:.65rem; padding: 1px 6px;">${areaLabelFn(t.area).slice(0,3)}</span>`;
    const nameLabel = isDisabled
      ? `<strong style="text-decoration:line-through; color:var(--ink-muted);">${htmlEscape(t.name)}</strong> · außer Betrieb`
      : `<strong>${htmlEscape(t.name)}</strong> · ${t.seats}P ${areaTag}`;
    rows += `
      <div class="timeline-row ${isDisabled ? 'tl-row-disabled' : ''}">
        <div>${nameLabel}</div>
        <div class="timeline-slots">${gridHtml.join('')}<div class="tl-bars">${bars.join('')}</div></div>
      </div>
    `;
  }
  // S217 Cut 2 — gemeinsames Bereich-Segment über der Timeline (identisch zum
  // Snapshot; jeder Klick rendert beide Karten neu).
  const availAreas = enabledSnapAreas(settings);
  const segHead = `<div class="occ-tl-head">${_areaSegHtml(areaId, availAreas)}</div>`;
  const body = tables.length
    ? `<div class="timeline" style="--slot-cols:${slotCount}">${rows}</div>`
    : `<p class="tc-hint" style="padding:14px; color:var(--ink-soft);">Keine Tische in diesem Bereich.</p>`;
  wrap.innerHTML = segHead + body;
}

export function renderOccupancySnapshot() {
  const state = getState();
  const time = (document.getElementById('snap-time') && document.getElementById('snap-time').value) || occupancySnapTime || nowSnappedToHalfHour();
  const wrap = document.getElementById('snapshot-wrap');
  if (!wrap) return;
  const occupiedIds = getOccupiedTableIds(occupancyDate, time, getCurrentSlotDuration());
  const reservationsAt = (state.reservations || []).filter(r => {
    if (r.status === 'declined' || r.status === 'cancelled' || r.deletedAt || r.date !== occupancyDate) return false;
    return reservationOverlaps(r, { date: occupancyDate, time, durationMin: getCurrentSlotDuration() });
  });

  // S199 Cut 2 — nur EIN Bereich (3er-Segment). Wahl aus dem gemeinsamen Modul-State
  // auflösen (Fallback Innen, falls die Präferenz deaktiviert/unbekannt ist) und
  // normalisiert zurückschreiben, damit ein deaktivierter Bereich nicht „hängen"
  // bleibt. S217 Cut 2 — kein localStorage mehr (Reload → Innen; Sitzung behält Wahl).
  const areaId = resolveSnapArea(occupancySnapArea, state.settings);
  if (areaId !== occupancySnapArea) occupancySnapArea = areaId;
  const availAreas = enabledSnapAreas(state.settings);
  const enabled = areaId === 'outdoor' ? state.settings.outdoorEnabled
                : areaId === 'club'    ? state.settings.clubEnabled : true;

  const tables = (state.tables || []).filter(t => t.area === areaId);
  const activeTables = tables.filter(t => t.disabled !== true);
  const cap = activeTables.reduce((s, t) => s + t.seats, 0);
  const busy = activeTables.filter(t => occupiedIds.has(t.id)).reduce((s, t) => s + t.seats, 0);
  const disabledCount = tables.length - activeTables.length;

  // Struktur-Element-Ebene (Cut 1-Daten) als statischer Hintergrund UNTER den
  // Kacheln — gleiche Geometrie wie im Editor, aber pointer-events:none, ohne
  // Raster/Handles. Canvas-Größe aus layout_areas (sonst Default 800×500).
  const size = canvasSizeForArea(state, areaId);
  const els = elementsForArea(state, areaId);
  const elLayer = els.map(renderElementStatic).join('');

  // Kachel-Rendering identisch zu bisher (nur als lokale Funktion faktorisiert).
  const renderSnapTile = (t) => {
    const isDisabled = t.disabled === true;
    const sharing = !isDisabled
      ? getTableSharingState(t.id, occupancyDate, time, getCurrentSlotDuration())
      : { walkIns: [], hasReservation: false, freeSeats: t.seats, isFullyBlocked: false };
    const res = sharing.hasReservation ? reservationsAt.find(r => (r.assignedTables || []).includes(t.id)) : null;
    const tableWalkIns = sharing.walkIns;
    const hasWalkIn = tableWalkIns.length > 0;
    const isShared = hasWalkIn && tableWalkIns.length > 1;
    const cls = isDisabled
      ? 'disabled-table'
      : (res ? (isConfirmedSlot(res) ? 'occupied' : 'reserved')
             : (hasWalkIn ? 'walkin-active' : ''));
    let comboTag = '';
    if (res && res.assignedTables && res.assignedTables.length > 1) {
      const others = res.assignedTables.filter(id => id !== t.id)
        .map(id => (state.tables.find(x => x.id === id) || {}).name || '?');
      comboTag = `<div class="tile-badge combo-badge">↔ ${htmlEscape(others.join(', '))}</div>`;
    }
    let chairTag = '';
    const uc = res ? (Number(res.extraChairsUsed) || (res.needsExtraChair ? 1 : 0)) : 0;
    if (uc > 0) chairTag = `<div class="tile-badge chair-badge">+${uc}🪑</div>`;
    // S239 Cut A0 — die Kachel zeigt nach dem Check-in die BELEGUNG (Name aus der
    // Reservierung, Überfällig-Uhr, „Rechnung offen"); dieses Badge sagt, woher
    // sie kommt. `capacity.js` lässt eingecheckte Reservierungen bewusst aus
    // Säule 1 fallen, damit der Tisch nicht doppelt gehalten wird.
    const fromReservation = tableWalkIns.some(o => o && o.reservationId);
    const resTag = fromReservation
      ? '<div class="tile-badge res-checkin-badge" title="Aus Reservierung eingecheckt">📅</div>'
      : '';
    // S243 Cut A — Schloss 🔓/🔒. Nur an belegten Tischen: ohne Belegung gibt es
    // nichts freizugeben (der Scan liefe in Cut B in „no_occupancy").
    // Bei geteilten Tischen zählt der SCHLECHTESTE Zustand — „🔓" darf nicht
    // suggerieren, dass alle Gruppen bestellen können, wenn eine gesperrt ist.
    // S244 Cut B — „📱": an diesem Tisch liegt ein Gast-Entwurf (noch nicht
    // abgesendet) oder eine vom Gast selbst aufgegebene Bestellung. Der Service
    // sieht damit auf einen Blick, wo gerade ohne ihn getippt wird.
    const guestDraft = tableWalkIns.map(_guestDraftOf).find(Boolean);
    const guestOrder = tableWalkIns.some(o =>
      (typeof window._findOpenOrderForContainer === 'function')
      && isTableSelfOrder(window._findOpenOrderForContainer(o.id, null) || {}));
    const guestTag = (guestDraft || guestOrder)
      ? `<div class="tile-badge selforder-guest" title="${htmlEscape(guestDraft
            ? `Gast-Entwurf: ${guestDraft.lines} ${guestDraft.lines === 1 ? 'Position' : 'Positionen'} — noch nicht abgesendet`
            : 'Bestellung kam vom Handy der Gäste')}">📱</div>`
      : '';
    // S245 Cut C — offene Rufe an DIESEM Tisch. Sie stehen neben 📱/🔓, weil sie
    // eine andere Frage beantworten: nicht „darf hier bestellt werden", sondern
    // „wartet hier gerade jemand auf mich".
    // S246 (T2) — die Badges stehen in einem eigenen Behaelter mit
    // `data-signal-tile`. Das Band aktualisierte sich live, die Kachel erst beim
    // naechsten Plan-Render: nach einem „Erledigt" blieb 🔔 minutenlang stehen.
    // Jetzt patcht `_patchTileSignalBadges` genau diesen Behaelter — ohne die
    // Kachel neu zu bauen (Fokus, Scroll und offene Modals bleiben).
    const callTag = `<span class="tile-signals" data-signal-tile="${htmlEscape(t.id)}"
      >${_signalBadgesHtml(t.id)}</span>`;
    let selfOrderTag = '';
    if (hasWalkIn) {
      const states = tableWalkIns.map(_selfOrderStateOf);
      const worst = states.find(s => s !== 'open') || 'open';
      const view = selfOrderView(worst);
      const title = isShared
        ? `${view.label} (${states.filter(s => s === 'open').length}/${states.length} Gruppen frei)`
        : view.label;
      selfOrderTag = `<div class="tile-badge selforder-badge${worst === 'open' ? ' is-open' : ''}" title="${htmlEscape(title)}">${view.icon}</div>`;
    }
    const displayName = htmlEscape(t.name) + (isDisabled ? ' 🚫' : '');
    const totalWalkInGuests = tableWalkIns.reduce((s, o) => s + (Number(o.guests) || 0), 0);
    const anyOverdue = tableWalkIns.some(o => isOccupancyOverdue(o));
    // S233 Ticket A — „⏱ +N min" (Minuten über dem geplanten Sitzdauer-Ende) +
    // „Rechnung offen", wenn an einer überfälligen Belegung noch eine offene
    // (unbezahlte) Order hängt (genau der S232-Fall). Read aus dem Cache über den
    // bestehenden Container-Resolver — kein neuer Query, kein DB-Touch.
    const overdueMins = tableWalkIns.reduce((mx, o) => Math.max(mx, occupancyOverdueMinutes(o)), 0);
    const overdueHasOpenOrder = anyOverdue && tableWalkIns.some(o =>
      (typeof window._findOpenOrderForContainer === 'function') && window._findOpenOrderForContainer(o.id, null));
    const overdueLabel = anyOverdue
      ? ` · ⏱ +${overdueMins} min${overdueHasOpenOrder ? ' · Rechnung offen' : ''}`
      : '';
    const displayInfo = isDisabled
      ? '<div class="tile-status-line">außer Betrieb</div>'
      : res
        ? `<div class="tile-status-line">${res.guests}P</div>`
        : isShared
          ? `<div class="tile-status-line" style="color:${anyOverdue ? 'var(--danger, #b94a48)' : 'var(--accent-dark)'};">${anyOverdue ? '⏰' : '🤝'} ${totalWalkInGuests}/${t.seats}P · ${tableWalkIns.length} Gäste-Gruppen${overdueLabel}</div>`
          : hasWalkIn
            ? `<div class="tile-status-line" style="color:${anyOverdue ? 'var(--danger, #b94a48)' : 'var(--accent-dark)'};">${anyOverdue ? '⏰' : '🪑'} ${tableWalkIns[0].guests}P${anyOverdue ? overdueLabel : (sharing.freeSeats > 0 ? ` · ${sharing.freeSeats} frei` : '')}</div>`
            : `<div class="tile-status-line free">frei · ${t.seats}P</div>`;
    const clickHandler = isDisabled
      ? ''
      : (res ? `onclick="openOccupancyResModal('${res.id}')"`
            : (hasWalkIn ? `onclick="openOccupancyTableWalkInsModal('${htmlEscape(t.id)}')"`
                        : `onclick="openOccupancyTableModal('${t.id}')"`));
    return `
      <div class="table-node seats-${Math.min(12, t.seats)} shape-${t.shape || 'rectangular'} ${cls}"
           style="left:${t.x}px; top:${t.y}px; cursor:${isDisabled ? 'default' : 'pointer'};"
           ${clickHandler}>
        <div class="tname">${displayName}</div>
        ${displayInfo}
        ${comboTag}
        ${chairTag}
        ${resTag}
        ${selfOrderTag}
        ${guestTag}
        ${callTag}
      </div>`;
  };

  // 3er-Segment (nur verfügbare Bereiche). Active-State aus dem gemeinsamen Modul-
  // State → überlebt Realtime-Re-Render (kein View-Hijack). S217 Cut 2: identische
  // Segment-Factory wie die Timeline.
  const seg = _areaSegHtml(areaId, availAreas);

  const tilesHtml = tables.length
    ? tables.map(renderSnapTile).join('')
    : `<p class="tc-hint" style="padding:14px; color:var(--ink-soft);">Keine Tische in diesem Bereich.</p>`;

  // S207-HOTFIX 3+4 — geteilter fit-skalierbarer Canvas, auf den Inhalt gecroppt.
  // maxH:0 = nur Breiten-Fit (in-page, kein Modal-Höhen-Deckel). tables inkl. disabled
  // (die werden gerendert) → gehen in die Bounding-Box ein.
  const bbox = contentBBox(els, tables, 24) || { x: 0, y: 0, w: size.w, h: size.h };
  _sideloadTableQrInfo();
  wrap.innerHTML = `
    <div id="occ-signal-band"></div>
    <div class="occ-snap-head">
      ${seg}
      <div class="occ-snap-count">${busy} / ${cap} Plätze belegt${disabledCount > 0 ? ` · ${disabledCount} außer Betrieb` : ''}</div>
    </div>
    ${planCanvasHtml({
      canvasW: bbox.w, canvasH: bbox.h, cropX: bbox.x, cropY: bbox.y, maxH: 0,
      outerClass: 'occ-snap-scroll', innerClass: `occ-snap-canvas ${enabled ? '' : 'disabled'}`,
      innerHtml: elLayer + tilesHtml
    })}
  `;
  fitAllPlans(wrap);
  // Band NACH dem innerHTML mounten — der Container entsteht gerade eben.
  // `mountSignalBand` ist idempotent genug: es registriert einen Zeichner und
  // liefert bei jeder Realtime-Änderung nach, ohne den Plan neu zu bauen.
  _mountBandOnce('occ-signal-band');
}

/**
 * S245 Cut C — offene Rufe an DIESEM Tisch. Sie stehen neben 📱/🔓, weil sie eine
 * andere Frage beantworten: nicht „darf hier bestellt werden", sondern „wartet
 * hier gerade jemand auf mich".
 */
function _signalBadgesHtml(tableId) {
  return signalsForTable(openSignals(), tableId)
    .filter(sg => sg.kind === 'call_service' || sg.kind === 'call_pay')
    .map(sg => `<div class="tile-badge signal-badge ${sg.kind === 'call_pay' ? 'is-pay' : 'is-service'}"
           title="${htmlEscape(sg.kind === 'call_pay' ? 'Gast bittet um die Rechnung' : 'Gast hat den Service gerufen')}">${sg.kind === 'call_pay' ? '💶' : '🔔'}</div>`)
    .join('');
}

/** S246 (T2) — Badges aller sichtbaren Kacheln nachziehen, nichts sonst. */
export function _patchTileSignalBadges() {
  const nodes = document.querySelectorAll('[data-signal-tile]');
  for (const el of nodes) {
    const next = _signalBadgesHtml(el.getAttribute('data-signal-tile'));
    if (el.innerHTML !== next) el.innerHTML = next;
  }
}

/* Je Container nur EIN Zeichner — `renderOccupancySnapshot` läuft bei jedem
   Realtime-Tick, ohne diesen Guard sammelten sich die Listener. */
const _mountedBands = new Map();
function _mountBandOnce(containerId) {
  if (_mountedBands.has(containerId)) {
    // Container wurde neu gebaut → einmal nachzeichnen reicht.
    renderSignalBandInto(containerId, _tableNameById);
    return;
  }
  _mountedBands.set(containerId, mountSignalBand(containerId, _tableNameById));
  // S246 (T2) — dasselbe Ereignis, das das Band zeichnet, zieht die
  // Kachel-Badges mit. Ohne das hinkt die Kachel dem Band hinterher.
  if (!_badgePatchBound) {
    _badgePatchBound = true;
    onSignalsChanged(_patchTileSignalBadges);
  }
}
let _badgePatchBound = false;

/** Tischname zu einer table_id — das Band zeigt „Tisch 4", nicht „t4". */
function _tableNameById(tableId) {
  const t = (getState().tables || []).find(x => x.id === tableId);
  return t ? t.name : null;
}

/** S199 Cut 2 / S217 Cut 2 — Bereich wechseln. EINE gemeinsame Wahl für beide
 *  Karten: State in die Modul-Var (kein localStorage; Reload → Innen) und BEIDE
 *  Karten (Timeline + Snapshot) non-destruktiv neu zeichnen, damit die Segmente
 *  synchron bleiben, egal welches geklickt wurde. */
export function setOccupancySnapArea(area) {
  occupancySnapArea = area;
  renderTimeline();
  renderOccupancySnapshot();
}

export function openOccupancyResModal(resId) {
  const state = getState();
  const statusBadgeFn = (typeof window.statusBadge === 'function') ? window.statusBadge : (() => '');
  const r = state.reservations.find(x => x.id === resId);
  if (!r) return;
  const tableNames = (r.assignedTables || []).map(id => (state.tables.find(t => t.id === id) || {}).name || '?').join(', ');
  const endTime = minutesToTime((timeToMinutes(r.time) + getReservationDuration(r)) % (24*60));
  const uc = Number(r.extraChairsUsed) || (r.needsExtraChair ? 1 : 0);
  const total = preorderTotal(r.preorder);
  const preorderItems = normalizePreorderItems(r.preorder);
  const specialReq = preorderSpecialRequests(r.preorder);
  // S134c (Befund 1+4) — manuelle Tisch-Freigabe auch für Reservierungs-Belegungen
  // (setzt table_released_at=now() über den phase_100-Pfad). Nur sinnvoll, wenn die
  // Reservierung HEUTE läuft (Start ≤ jetzt) und noch nicht freigegeben ist — sonst
  // würde eine künftige Buchung fälschlich entblockt.
  const nowMin = (() => { const n = new Date(); return n.getHours() * 60 + n.getMinutes(); })();
  const alreadyReleased = !!r.tableReleasedAt;
  const canReleaseNow = !alreadyReleased && r.date === todayISO() && timeToMinutes(r.time) <= nowMin;
  const releaseRow = alreadyReleased
    ? `<div style="background:var(--green-soft);border-left:3px solid var(--green);padding:6px 10px;margin:10px 0 0;font-size:.85rem;">✓ Tisch freigegeben um ${htmlEscape(new Date(r.tableReleasedAt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }))} Uhr — Belegung verkürzt.</div>`
    : '';
  // S239 Cut A0 — „✅ Gäste sind da": erzeugt die Belegung an der Reservierung.
  // Nach dem Check-in führt derselbe Platz zur Belegung (dort hängen Bestellen,
  // Kassieren, Freigeben). Rollen-Gate spiegelt die RPC (manager/service).
  const checkinOcc = findCheckinOccupancy(r.id, state.tableOccupancies);
  const checkinBtnRaw = reservationCheckinButton(r, checkinOcc);
  const checkinBtn = (checkinBtnRaw.mode === 'checkin' && !_mayCheckin())
    ? { show: false, mode: 'checkin', label: '', occupancyId: null }
    : checkinBtnRaw;
  const arrivedRow = (checkinOcc && r.arrivedAt)
    ? `<div style="background:var(--green-mist, var(--green-soft));border-left:3px solid var(--green);padding:6px 10px;margin:10px 0 0;font-size:.85rem;">✅ Gäste sind da seit ${htmlEscape(new Date(r.arrivedAt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }))} Uhr — Tisch ist belegt.</div>`
    : '';
  const html = `
    <div class="modal-header">
      <h2>${htmlEscape(r.name)} · ${r.guests}P</h2>
      <button class="modal-close" onclick="closeModal()">×</button>
    </div>
    <div class="field-row">
      <div><strong>Status:</strong> ${statusBadgeFn(r)}</div>
      <div><strong>Quelle:</strong> ${htmlEscape(r.source || 'online')}</div>
    </div>
    <div class="field-row">
      <div><strong>Zeit:</strong> ${r.time}–${endTime} Uhr (${getReservationDuration(r)} Min.)</div>
      <div><strong>Datum:</strong> ${fmtDate(r.date)}</div>
    </div>
    <div class="field-row">
      <div><strong>Tische:</strong> ${htmlEscape(tableNames)}${r.tablesCombined ? ' (kombiniert)' : ''}</div>
      <div><strong>Zusatzstühle:</strong> ${uc}</div>
    </div>
    ${r.phone || r.email ? `
      <div class="field-row">
        ${r.phone ? `<div><strong>Telefon:</strong> <a href="tel:${htmlEscape(r.phone)}">${htmlEscape(r.phone)}</a></div>` : '<div></div>'}
        ${r.email ? `<div><strong>E-Mail:</strong> <a href="mailto:${htmlEscape(r.email)}">${htmlEscape(r.email)}</a></div>` : '<div></div>'}
      </div>` : ''}
    ${r.notes ? `<div class="field"><strong>Bemerkung:</strong><div class="notes">${htmlEscape(r.notes)}</div></div>` : ''}
    ${preorderItems.length ? `
      <div class="field">
        <strong>Vorbestellung:</strong>
        <div class="notes">
          ${preorderItems.map(it => `
            ${it.qty}× ${htmlEscape(it.displayName)} — ${fmtEUR(it.lineTotal)}
            ${it.note ? `<div style="font-size:.85rem; color:var(--ink-soft); margin-left:14px;">${htmlEscape(it.note)}</div>` : ''}
          `).join('<br>')}
          <div style="margin-top: 6px; font-weight: 600;">Summe: ${fmtEUR(total)}</div>
          ${specialReq ? `<div style="margin-top: 8px; padding: 6px 8px; background: var(--warn-soft); border-radius: 4px;"><strong>Sonderwunsch:</strong> ${htmlEscape(specialReq)}</div>` : ''}
        </div>
      </div>
    ` : ''}
    ${arrivedRow}
    ${releaseRow}
    <div class="btn-row" style="margin-top: 16px;">
      ${checkinBtn.show
        ? (checkinBtn.mode === 'checkin'
            ? `<button class="btn btn-primary" onclick="checkinReservationAndOpen('${htmlEscape(r.id)}')" title="Gäste sind eingetroffen — Tisch wird belegt, ab hier wie ein Walk-In">${checkinBtn.label}</button>`
            : `<button class="btn btn-primary" onclick="closeModal(); openOccupancyWalkInModal('${htmlEscape(checkinBtn.occupancyId)}')">${checkinBtn.label}</button>`)
        : ''}
      <button class="btn ${checkinBtn.show ? 'btn-ghost' : 'btn-primary'}" onclick="closeModal(); openReservationDetail('${r.id}');">Detail öffnen</button>
      ${canReleaseNow ? `<button class="btn btn-ghost" onclick="releaseReservationTableFromModal('${htmlEscape(r.id)}')" style="color: var(--danger, #b94a48);">✓ Tisch jetzt freigeben</button>` : ''}
      <button class="btn btn-ghost" onclick="closeModal()">Schließen</button>
    </div>
  `;
  document.getElementById('modal-content').innerHTML = html;
  document.getElementById('modal-overlay').classList.add('active');
}

export function openOccupancyTableModal(tableId) {
  const state = getState();
  const areaLabelFn = (typeof window.areaLabel === 'function') ? window.areaLabel : (a => a || '');
  const t = state.tables.find(x => x.id === tableId);
  if (!t) return;
  const ec = tableExtraChairs(t);
  const time = (document.getElementById('snap-time') && document.getElementById('snap-time').value) || occupancySnapTime || nowSnappedToHalfHour();
  const html = `
    <div class="modal-header">
      <h2>Tisch ${htmlEscape(t.name)} · frei</h2>
      <button class="modal-close" onclick="closeModal()">×</button>
    </div>
    <div class="field-row">
      <div><strong>Bereich:</strong> ${areaLabelFn(t.area)}</div>
      <div><strong>Sitzplätze:</strong> ${t.seats}${ec > 0 ? ` (+${ec} Zusatzstuhl${ec > 1 ? 'e' : ''} möglich)` : ''}</div>
    </div>
    <div class="field-row">
      <div><strong>Kombinierbar:</strong> ${t.extendable === false ? 'nein (fix)' : 'ja'}</div>
      <div><strong>Status:</strong> frei für ${occupancyDate === todayISO() ? 'heute' : fmtDate(occupancyDate)} um ${time} Uhr</div>
    </div>
    <p style="margin: 16px 0 8px 0; color: var(--ink-soft); font-size: .9rem;">
      Möchtest du diesen Tisch direkt einem spontanen Gast zuweisen?
    </p>
    <div class="btn-row" style="margin-top: 12px;">
      <button class="btn btn-primary" onclick="startWalkInAtTable('${t.id}')">+ Walk-In hier setzen</button>
      <button class="btn btn-ghost" onclick="closeModal()">Schließen</button>
    </div>
  `;
  document.getElementById('modal-content').innerHTML = html;
  document.getElementById('modal-overlay').classList.add('active');
}

/** S60 W4.3 — Walk-In über Klick auf freien Tisch in Belegungs-View setzen.
 *  Routet jetzt durch das neue 3-Step-Modal aus admin-order-entry.js (Hier-Essen
 *  → Daten → Tisch[preselect] → Decision). Single-Source für Walk-In-Erfassung,
 *  alter walkin.js-Form wird nicht mehr verwendet. */
export function startWalkInAtTable(tableId) {
  closeModal();
  if (typeof window.openAdminOrderEntry !== 'function'
      || typeof window.adminOrderSelectService !== 'function'
      || typeof window.adminOrderSetTable !== 'function') {
    // Fallback auf altes Formular, falls neuer Adapter (noch) nicht geladen
    if (typeof window.openWalkInForm === 'function') {
      window.openWalkInForm({ preselectTableId: tableId });
    }
    return;
  }
  // Modal öffnen, Service-Typ auf dine-in, Tisch vorausgewählt, direkt zu Step 3 (Decision).
  window.openAdminOrderEntry();
  window.adminOrderSelectService('dine-in');     // step → 2 (Daten)
  // Tisch im Hintergrund schon vormerken; der Operator bestätigt erst Daten, dann ist
  // im Tisch-Vorschlag (Step 3) der gewählte Tisch automatisch markiert.
  window.adminOrderSetTable(tableId);
}

/**
 * S243 Cut A — Toggle-Zeile „Handy-Bestellung am Tisch" für das Belegungs-Modal.
 *
 * Der Schalter zeigt IMMER den Operator-Schalter (`selfOrderEnabled`), nicht den
 * berechneten Zustand: „überfällig" oder „Reservierung steht an" sind keine
 * Dinge, die man hier umlegen kann — sie stehen als Hinweiszeile darunter.
 * Andernfalls hätte der Service einen Schalter vor sich, der nach dem Klick in
 * die Ausgangsstellung zurückspringt.
 */
function _selfOrderToggleHtml(occ) {
  if (!occ) return '';
  const state = _selfOrderStateOf(occ);
  const view = selfOrderView(state);
  const enabled = occ.selfOrderEnabled !== false;
  const may = _maySelfOrder();
  // Auto-Sperre trotz eingeschaltetem Schalter → erklären, warum trotzdem 🔒.
  const autoHint = (enabled && state !== 'open')
    ? `<div class="hint" style="margin-top:6px;">${view.icon} ${htmlEscape(view.label)} — der Schalter steht auf „frei", das System sperrt gerade automatisch.</div>`
    : '';
  const denied = may ? '' :
    `<div class="hint" style="margin-top:6px;">Nur Manager und Service dürfen das umstellen.</div>`;
  return `
    <div class="toggle-row" style="margin:10px 0; padding:8px 10px; background:var(--bg-soft); border-radius:8px;">
      <div style="min-width:0; flex:1 1 auto;">
        <div class="label">📱 Handy-Bestellung am Tisch</div>
        <div class="desc">Gäste dürfen über den Tisch-QR selbst bestellen.</div>
        ${autoHint}${denied}
      </div>
      <label class="toggle" style="flex:0 0 auto;">
        <input type="checkbox" ${enabled ? 'checked' : ''} ${may ? '' : 'disabled'}
               onchange="toggleOccupancySelfOrder('${htmlEscape(occ.id)}', this.checked)">
        <span class="toggle-slider"></span>
      </label>
    </div>`;
}

/**
 * S243 Cut A — Schloss umlegen. Server-fest über `set_occupancy_self_order`;
 * das UI-Gate hier ist nur die Vorab-Höflichkeit.
 *
 * Nach Erfolg wird das Modal neu gezeichnet (der Toggle-Zustand kommt aus dem
 * gepatchten Cache) und der Snapshot aktualisiert, damit das Kachel-Badge
 * sofort mitzieht — ohne auf das Realtime-Echo zu warten.
 */
export async function toggleOccupancySelfOrder(occupancyId, enabled) {
  if (!occupancyId) return;
  if (!_maySelfOrder()) {
    if (typeof window.showToast === 'function') {
      window.showToast('Für diese Aktion fehlt dir die Berechtigung.', 'error');
    }
    // Checkbox zurückdrehen — sonst zeigt sie einen Zustand, den es nicht gibt.
    const occ = (getState().tableOccupancies || []).find(o => o.id === occupancyId);
    if (occ) openOccupancyWalkInModal(occupancyId);
    return;
  }
  try {
    await window.App.storage.tableOccupancies.setSelfOrder(occupancyId, !!enabled);
    if (typeof window.showToast === 'function') {
      window.showToast(enabled
        ? 'Handy-Bestellung am Tisch freigegeben.'
        : 'Handy-Bestellung am Tisch gesperrt.', 'success');
    }
  } catch (err) {
    if (typeof window.showToast === 'function') {
      window.showToast('Fehler: ' + (err.message || err), 'error');
    }
    console.error('[toggleOccupancySelfOrder]', err);
  }
  // Auch im Fehlerfall neu zeichnen: die Checkbox muss den DB-Stand zeigen,
  // nicht den Klick, der nicht durchkam.
  _rerenderOpenOccupancyModal(occupancyId);
  renderOccupancySnapshot();
}

/** Das GERADE OFFENE Belegungs-Modal neu zeichnen — Einzel- oder Sharing-Sicht.
 *  Ohne diese Unterscheidung würde ein Toggle in der Sharing-Liste den Operator
 *  in die Einzelsicht einer Gruppe werfen (View-Hijack, Memory
 *  feedback_gg_no_view_hijack). */
function _rerenderOpenOccupancyModal(occupancyId) {
  const overlay = document.getElementById('modal-overlay');
  if (!overlay || !overlay.classList.contains('active')) return;
  const marker = document.getElementById('occ-multi-modal-marker');
  if (marker) {
    openOccupancyTableWalkInsModal(marker.getAttribute('data-table-id'));
  } else {
    openOccupancyWalkInModal(occupancyId);
  }
}

/** Kompakter Schloss-Schalter für eine Zeile der Sharing-Liste. */
function _selfOrderRowButton(occ) {
  const state = _selfOrderStateOf(occ);
  const view = selfOrderView(state);
  const enabled = occ.selfOrderEnabled !== false;
  if (!_maySelfOrder()) {
    return `<span class="btn btn-ghost btn-sm" style="opacity:.55; cursor:default;" title="${htmlEscape(view.label)} — nur Manager/Service dürfen schalten">${view.icon}</span>`;
  }
  return `<button class="btn btn-ghost btn-sm"
            onclick="toggleOccupancySelfOrder('${htmlEscape(occ.id)}', ${enabled ? 'false' : 'true'})"
            title="${htmlEscape(view.label)} — klicken zum ${enabled ? 'Sperren' : 'Freigeben'}">${view.icon}</button>`;
}

/** S60 W2 — Modal für einen aktiven Walk-In: Daten anzeigen + Bestellung aufnehmen
 *  ODER Walk-In beenden (Tisch frei machen). Wird vom Klick auf einen Walk-In-Tisch
 *  im Snapshot gerendert. */
export function openOccupancyWalkInModal(occupancyId) {
  const state = getState();
  const occ = (state.tableOccupancies || []).find(o => o.id === occupancyId);
  if (!occ) return;
  const table = (state.tables || []).find(t => t.id === occ.tableId);
  const tableName = (table && table.name) || occ.tableId;
  // S134c (Befund 1+4) — beendete Belegung schrumpft, verschwindet nicht:
  // neutrales Wording, reales Ende zeigen, KEIN „beenden"-Button mehr.
  const isCompleted = occ.status === 'completed';
  const startedStr = occ.startedAt
    ? new Date(occ.startedAt).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })
    : '—';
  const endedStr = occ.endedAt
    ? new Date(occ.endedAt).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })
    : '—';
  const elapsedMin = occ.startedAt
    ? Math.round((Date.now() - new Date(occ.startedAt).getTime()) / 60000)
    : 0;
  const realDurMin = (occ.startedAt && occ.endedAt)
    ? Math.max(0, Math.round((new Date(occ.endedAt).getTime() - new Date(occ.startedAt).getTime()) / 60000))
    : 0;
  const durationLine = isCompleted
    ? `<div><strong>Beendet:</strong> ${htmlEscape(endedStr)} (Dauer ${realDurMin} Min)</div>`
    : `<div><strong>Verweildauer:</strong> ${occ.durationMin || 90} Min · läuft seit ${elapsedMin} Min</div>`;
  const customerInfo = occ.customerId
    ? `<div style="background:var(--green-soft);border-left:3px solid var(--green);padding:6px 10px;margin:6px 0;font-size:.85rem;">✓ Verknüpft mit Stammkunde</div>` : '';
  // S196 Cut a — „überfällig"-Hinweis: geplante Sitzdauer abgelaufen, Belegung
  // aber noch aktiv. Verwaiste (ohne offene Order) werden nach +15 Min per
  // Auto-Freigabe beendet; bleibt eine offene Order, blockt der Tisch zu Recht.
  // S196 Cut b1 / S233 Ticket A — offene (unbezahlte) Order an dieser Belegung?
  // Read aus dem Cache über den bestehenden Container-Resolver (kein DB-Touch).
  const openOrder = (typeof window._findOpenOrderForContainer === 'function')
    ? window._findOpenOrderForContainer(occ.id, null) : null;
  const openOrderCents = openOrder
    ? ((openOrder.totalGrossCents != null ? openOrder.totalGrossCents : openOrder.total_gross_cents) || 0) : 0;
  const overdueMin = (!isCompleted) ? occupancyOverdueMinutes(occ) : 0;
  const overdueInfo = (!isCompleted && isOccupancyOverdue(occ))
    ? `<div style="background:var(--warn-soft);border-left:3px solid var(--danger, #b94a48);padding:6px 10px;margin:6px 0;font-size:.85rem;">⏰ <strong>Überfällig · ⏱ +${overdueMin} min</strong> — geplante Dauer (${occ.durationMin || 90} Min) überschritten. Ohne offene Order wird der Tisch automatisch freigegeben; sonst bitte kassieren oder manuell freigeben.</div>` : '';
  // S233 Ticket A — offene Rechnung prominent + direkter Kassieren-Absprung.
  const openOrderInfo = openOrder
    ? `<div style="background:var(--warn-soft);border-left:3px solid var(--danger, #b94a48);padding:6px 10px;margin:6px 0;font-size:.85rem;">🧾 <strong>Rechnung offen</strong> — ${fmtEUR(openOrderCents / 100)}. Bitte kassieren (Warenkorb öffnen) oder klären.</div>` : '';
  // Kontextabhängiger Primär-Button: offene Order → „🛒 Warenkorb öffnen / kassieren";
  // sonst „📋 Bestellung aufnehmen".
  const orderBtn = openOrder
    ? `<button class="btn btn-primary" onclick="closeModal(); openTableCart('${htmlEscape(openOrder.id)}')">🛒 Warenkorb öffnen / kassieren</button>`
    : `<button class="btn btn-primary" onclick="closeModal(); openOrderForOccupancy('${htmlEscape(occ.id)}')">📋 Bestellung aufnehmen</button>`;
  // S243 Cut A — Toggle-Zeile „Handy-Bestellung am Tisch". Nur an einer AKTIVEN
  // Belegung: `set_occupancy_self_order` lehnt beendete serverseitig ab, ein
  // Schalter dafür wäre eine Einladung in eine Fehlermeldung.
  const selfOrderRow = isCompleted ? '' : _selfOrderToggleHtml(occ);
  // S245 Cut C — offene Rufe dieser Belegung, direkt im Modal erledigbar. Wer
  // den Tisch ohnehin gerade offen hat, soll nicht ins Band zurueckwechseln.
  const occSignals = signalsForTable(openSignals(), occ.tableId)
    .filter(sg => sg.kind === 'call_service' || sg.kind === 'call_pay');
  const signalInfo = occSignals.length ? occSignals.map(sg => `
    <div style="background:var(--warn-soft);border-left:3px solid #2f5d8a;padding:6px 10px;margin:6px 0;font-size:.85rem;display:flex;align-items:center;justify-content:space-between;gap:8px;">
      <span>${sg.kind === 'call_pay' ? '💶' : '🔔'} <strong>${htmlEscape(sg.kind === 'call_pay' ? 'Rechnung angefragt' : 'Service gerufen')}</strong>${sg.guest_name ? ' · ' + htmlEscape(sg.guest_name) : ''}
        · ${htmlEscape(signalAgeText(sg.created_at))}</span>
      <button class="btn btn-ghost btn-sm" onclick="ackTableSignal('${htmlEscape(sg.id)}')">Erledigt</button>
    </div>`).join('') : '';
  const actions = isCompleted
    ? `<button class="btn btn-ghost" onclick="closeModal()">Schließen</button>`
    : `
      ${orderBtn}
      <button class="btn btn-ghost" onclick="endOccupancyFromModal('${htmlEscape(occ.id)}')" style="color: var(--danger, #b94a48);">✓ Tisch jetzt freigeben</button>
      <button class="btn btn-ghost" onclick="closeModal()">Schließen</button>`;
  const html = `
    <div class="modal-header">
      <h2>🪑 Tisch ${htmlEscape(String(tableName))} — Belegung${isCompleted ? ' (beendet)' : ''}</h2>
      <button class="modal-close" onclick="closeModal()">×</button>
    </div>
    <div class="field-row">
      <div><strong>Gäste:</strong> ${occ.guests}</div>
      ${durationLine}
    </div>
    <div class="field-row">
      <div><strong>Belegt seit:</strong> ${htmlEscape(startedStr)}</div>
      <div><strong>Quelle:</strong> ${htmlEscape(occ.source || 'walk-in')}</div>
    </div>
    ${occ.guestName ? `<div class="field-row"><div><strong>Name:</strong> ${htmlEscape(occ.guestName)}</div>${occ.guestPhone ? `<div><strong>Telefon:</strong> <a href="tel:${htmlEscape(occ.guestPhone)}">${htmlEscape(occ.guestPhone)}</a></div>` : '<div></div>'}</div>` : ''}
    ${customerInfo}
    ${signalInfo}
    ${overdueInfo}
    ${openOrderInfo}
    ${selfOrderRow}
    ${occ.notes ? `<div class="field"><strong>Bemerkung:</strong><div class="notes">${htmlEscape(occ.notes)}</div></div>` : ''}
    <div class="btn-row" style="margin-top: 16px; display: flex; gap: 8px; flex-wrap: wrap;">
      ${actions}
    </div>
  `;
  document.getElementById('modal-content').innerHTML = html;
  document.getElementById('modal-overlay').classList.add('active');
}

/** S60 W4.13 — Modal für alle Walk-Ins an einem Tisch (Sharing-Fall).
 *  Wenn nur 1 Walk-In: zeigt direkt das Einzel-Modal.
 *  Wenn mehrere Walk-Ins: Liste mit Auswahl + Sharing-Status. */
export function openOccupancyTableWalkInsModal(tableId) {
  const state = getState();
  const time = (document.getElementById('snap-time') && document.getElementById('snap-time').value) || occupancySnapTime || nowSnappedToHalfHour();
  const sharing = getTableSharingState(tableId, occupancyDate, time, getCurrentSlotDuration());
  const table = (state.tables || []).find(t => t.id === tableId);
  const tableName = (table && table.name) || tableId;
  if (sharing.walkIns.length === 0) return;
  if (sharing.walkIns.length === 1) {
    return openOccupancyWalkInModal(sharing.walkIns[0].id);
  }
  // Multi-Walk-In-Sharing-Anzeige
  const rows = sharing.walkIns.map(o => {
    const startedStr = o.startedAt ? new Date(o.startedAt).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' }) : '—';
    const elapsedMin = o.startedAt ? Math.round((Date.now() - new Date(o.startedAt).getTime()) / 60000) : 0;
    return `
      <div style="border: 1px solid var(--border); border-radius: 8px; padding: 10px 12px; margin-bottom: 8px; background: var(--bg-elev);">
        <div style="display:flex; justify-content:space-between; align-items:center; gap:8px;">
          <div>
            <strong>${o.guests} Gäste${o.guestName ? ' · ' + htmlEscape(o.guestName) : ''}</strong>
            <div style="font-size:.82rem; color: var(--ink-soft, #666);">
              ab ${htmlEscape(startedStr)} · läuft seit ${elapsedMin} Min · geplant ${o.durationMin || 90} Min
            </div>
            ${o.notes ? `<div style="font-size:.8rem; color: var(--ink-soft); font-style: italic; margin-top: 2px;">${htmlEscape(o.notes)}</div>` : ''}
          </div>
          <div style="display:flex; gap:6px;">
            ${_selfOrderRowButton(o)}
            ${(() => {
              const oo = (typeof window._findOpenOrderForContainer === 'function')
                ? window._findOpenOrderForContainer(o.id, null) : null;
              return oo
                ? `<button class="btn btn-ghost btn-sm" onclick="closeModal(); openTableCart('${htmlEscape(oo.id)}')" title="Warenkorb dieser Gäste-Gruppe">🛒 Warenkorb</button>`
                : `<button class="btn btn-ghost btn-sm" onclick="closeModal(); openOrderForOccupancy('${htmlEscape(o.id)}')" title="Bestellung für diesen Walk-In aufnehmen">📋 Bestellen</button>`;
            })()}
            <button class="btn btn-ghost btn-sm" onclick="endOccupancyFromModal('${htmlEscape(o.id)}')" title="Walk-In beenden" style="color: var(--danger, #b94a48);">✓ Beenden</button>
          </div>
        </div>
      </div>
    `;
  }).join('');
  const html = `
    <!-- S243: Marker für _rerenderOpenOccupancyModal — sagt dem Schloss-Handler,
         dass die SHARING-Sicht offen ist (sonst landet der Operator nach dem
         Klick in der Einzelsicht einer Gruppe). -->
    <div id="occ-multi-modal-marker" data-table-id="${htmlEscape(tableId)}" hidden></div>
    <div class="modal-header">
      <h2>🤝 Tisch ${htmlEscape(String(tableName))} — geteilt mit ${sharing.walkIns.length} Walk-Ins</h2>
      <button class="modal-close" onclick="closeModal()">×</button>
    </div>
    <div style="background:var(--warn-soft);border-left:3px solid var(--accent);padding:10px 12px;margin-bottom:14px;font-size:.92rem;">
      Tisch hat ${sharing.tableSeats} Plätze · ${sharing.occupiedSeats} belegt · <strong>${sharing.freeSeats} frei</strong>
    </div>
    ${rows}
    <div class="btn-row" style="margin-top: 12px; display: flex; gap: 8px;">
      ${sharing.freeSeats > 0 ? `
        <button class="btn btn-primary" onclick="closeModal(); startWalkInAtTable('${htmlEscape(tableId)}')">
          + Weiteren Walk-In an diesen Tisch (${sharing.freeSeats} ${sharing.freeSeats===1?'Platz':'Plätze'} frei)
        </button>
      ` : ''}
      <button class="btn btn-ghost" onclick="closeModal()">Schließen</button>
    </div>
  `;
  document.getElementById('modal-content').innerHTML = html;
  document.getElementById('modal-overlay').classList.add('active');
}

/** S60 W2 — Walk-In beenden (status='completed'). */
export async function endOccupancyFromModal(occupancyId) {
  if (!occupancyId) return;
  // S60 W4.4 — Permission-Gate: nur User mit walkin.update dürfen beenden
  if (typeof window !== 'undefined' && window.App && window.App.auth
      && typeof window.App.auth.can === 'function'
      && !window.App.auth.can('walkin.update')) {
    if (typeof window.showToast === 'function') {
      window.showToast('Für diese Aktion fehlt dir die Berechtigung.', 'error');
    }
    return;
  }
  if (!(await window.styledConfirm({ title: 'Belegung beenden?', body: 'Der Tisch wird ab jetzt wieder frei; die Belegung bleibt (verkürzt) sichtbar.', confirmLabel: 'Beenden' })).confirmed) return;
  try {
    await window.App.storage.tableOccupancies.end(occupancyId);
    if (typeof window.showToast === 'function') window.showToast('Belegung beendet — Tisch frei.', 'success');
    closeModal();
    renderOccupancy();
  } catch (err) {
    console.error('[endOccupancyFromModal]', err);
    if (typeof window.showToast === 'function') {
      window.showToast('Fehler: ' + (err.message || err), 'error');
    }
  }
}

/** S134c (Befund 1+4) — manuelle Tisch-Freigabe einer Reservierungs-Belegung.
 *  Setzt `table_released_at = now()` über den vorhandenen phase_100-Pfad
 *  (Adapter-Direkt-Update); capacity.effectiveReservationEndMin + die Timeline
 *  schrumpfen den Balken auf jetzt, die Reservierung bleibt sichtbar. */
export async function releaseReservationTableFromModal(resId) {
  if (!resId) return;
  if (!(await window.styledConfirm({ title: 'Tisch jetzt freigeben?', body: 'Die Reservierung bleibt sichtbar (verkürzt), der Tisch wird ab jetzt wieder frei.', confirmLabel: 'Freigeben' })).confirmed) return;
  try {
    await window.App.storage.reservations.update(resId, { tableReleasedAt: new Date().toISOString() });
    if (typeof window.showToast === 'function') window.showToast('Tisch freigegeben.', 'success');
    closeModal();
    renderOccupancy();
  } catch (err) {
    console.error('[releaseReservationTableFromModal]', err);
    if (typeof window.showToast === 'function') {
      window.showToast('Fehler: ' + (err.message || err), 'error');
    }
  }
}

/** S60 W2 — Bestellung nachträglich für einen aktiven Walk-In aufnehmen.
 *  Öffnet admin-order-entry direkt im Items-Step (5) mit pre-set tableId +
 *  dineInForm aus dem Walk-In. Nach Submit wird die Order mit occupancy_id
 *  cross-verknüpft. */
export function openOrderForOccupancy(occupancyId) {
  // S60 W4.4 — Permission-Gate: order.create + walkin.update für Cross-Linking
  if (typeof window !== 'undefined' && window.App && window.App.auth
      && typeof window.App.auth.can === 'function'
      && !window.App.auth.can('order.create')) {
    if (typeof window.showToast === 'function') {
      window.showToast('Für diese Aktion fehlt dir die Berechtigung.', 'error');
    }
    return;
  }
  const state = getState();
  const occ = (state.tableOccupancies || []).find(o => o.id === occupancyId);
  if (!occ) return;
  if (typeof window.openAdminOrderEntry !== 'function') {
    if (typeof window.showToast === 'function') window.showToast('Order-Modal nicht verfügbar.', 'error');
    return;
  }
  // Pre-Set: Service-Type dine-in, tableId, dineInForm aus dem Walk-In, Skip auf Step 5 (Items)
  window.openAdminOrderEntry();
  // Setze interne State-Felder via window-State (admin-order-entry exportiert _state nicht direkt;
  // wir nutzen die public Setters)
  if (typeof window.adminOrderSelectService === 'function') {
    window.adminOrderSelectService('dine-in');
  }
  // Tisch setzen
  if (typeof window.adminOrderSetTable === 'function') {
    window.adminOrderSetTable(occ.tableId);
  }
  // dineInForm-Felder aus Walk-In übertragen
  if (typeof window.adminOrderSetDineInField === 'function') {
    window.adminOrderSetDineInField('guests', occ.guests);
    window.adminOrderSetDineInField('durationMin', occ.durationMin || 90);
    if (occ.guestName)  window.adminOrderSetDineInField('guestName', occ.guestName);
    if (occ.guestPhone) window.adminOrderSetDineInField('guestPhone', occ.guestPhone);
    if (occ.notes)      window.adminOrderSetDineInField('notes', occ.notes);
  }
  // Direkt zum Items-Step springen (skip Step 2/3, Daten + Tisch sind bereits gesetzt)
  if (typeof window.adminOrderDineInProceedToOrder === 'function') {
    setTimeout(() => window.adminOrderDineInProceedToOrder(), 0);
  }
  // occupancyId merken für Cross-Linking beim Submit
  window._pendingOccupancyIdForOrder = occupancyId;
}

// Backward-Compat
if (typeof window !== 'undefined') {
  Object.assign(window, {
    renderOccupancy,
    renderOccupancySnapshot,
    setOccupancySnapArea,   // S199 Cut 2 — Bereichs-Segment der Räume-Karte
    openOccupancyResModal,
    openOccupancyTableModal,
    startWalkInAtTable,
    // S60 W2 — Walk-In-Modal + Order-für-Walk-In-Flow
    openOccupancyWalkInModal,
    endOccupancyFromModal,
    releaseReservationTableFromModal,
    openOrderForOccupancy,
    // S60 W4.13 — Multi-Walk-In-Sharing-Modal
    openOccupancyTableWalkInsModal,
    // S243 Cut A — Schloss „Handy-Bestellung am Tisch"
    toggleOccupancySelfOrder
  });
  // occupancyDate + occupancySnapTime via getter/setter exponieren, da inline-onclicks sie direkt schreiben.
  Object.defineProperty(window, 'occupancyDate', {
    get() { return occupancyDate; },
    set(v) { occupancyDate = v; },
    configurable: true
  });
  Object.defineProperty(window, 'occupancySnapTime', {
    get() { return occupancySnapTime; },
    set(v) { occupancySnapTime = v; },
    configurable: true
  });
}
